import type { Ref } from 'vue'
import type { DisciplineOrderPayload } from './interfaces'

import { DisciplinePaymentOption } from './enums'
import { useDisciplinePaymentLinksStore } from './payment-links/store'
import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { PAYMENT_SUBSCRIPTION_TYPE } from './discipline-payment-calculate.hook'
import {
  useDisciplineOrderStoreApi,
  useDisciplineSubscriptionStoreApi,
} from '../api/payment-generate.hook'
import {
  formatPaymentDiscountAmount,
  formatPaymentPrice,
  formatPaymentValue,
  getPaymentProductLessons,
} from './discipline-payment-links'

interface DisciplinePaymentDiscountRow {
  label: string
  percent: number
  amount: string
}

interface DisciplineOneTimeSummary {
  lessonsInPackage: string
  totalPrice: string
  discount: DisciplinePaymentDiscountRow
  total: string
}

interface DisciplineInstallmentSummary {
  maxPace: string
  lessonsPerPayment: string
  firstPayment: string
  regularPayment: string
  qty: string
  lessonsInPackage: string
  totalPrice: string
  trialDiscount: DisciplinePaymentDiscountRow | null
  discount: DisciplinePaymentDiscountRow
  total: string
}

interface DisciplinePaymentSummaryReturn {
  refIsOneTime: ComputedRef<boolean>
  refIsCalculating: ComputedRef<boolean>
  refIsGenerating: Ref<boolean>
  refCanGenerate: ComputedRef<boolean>
  refOneTime: ComputedRef<DisciplineOneTimeSummary>
  refInstallment: ComputedRef<DisciplineInstallmentSummary>
  refCells: ComputedRef<string[]>
  generate: () => Promise<void>
}

const DISCOUNT_LABEL = 'Discount:'
const TRIAL_DISCOUNT_LABEL = 'First payment discount:'

export const useDisciplinePaymentSummary = (): DisciplinePaymentSummaryReturn => {
  const route = useRoute()
  const store = useDisciplinePaymentLinksStore()
  const disciplineStore = useDisciplineStore()
  const { canManageOrders, canManageSubscriptions } = useDisciplinePrivileges()

  const refIsGenerating = ref<boolean>(false)

  const refIsOneTime = computed<boolean>(() => store.isOneTime)
  const refIsCalculating = computed<boolean>(() => store.isCalculating)

  const refSelectedId = computed<number | null>(() => store.isOneTime ? store.getPackageId : store.getInstallmentId)

  const refCanGenerate = computed<boolean>(() => {
    const canWrite = store.isOneTime ? canManageOrders.value : canManageSubscriptions.value

    return canWrite && !!refSelectedId.value && !store.getCalculationError && !store.isCalculating && !refIsGenerating.value
  })

  const refOneTime = computed<DisciplineOneTimeSummary>(() => {
    const calculation = store.getOrderCalculation
    const product = store.getProducts.find(item => item.id === store.getPackageId) ?? null

    return {
      lessonsInPackage: formatPaymentValue(
        calculation?.lessons ?? getPaymentProductLessons(product, disciplineStore.getDetails?.code ?? null),
      ),
      totalPrice: formatPaymentPrice(calculation?.price),
      discount: {
        label: DISCOUNT_LABEL,
        percent: store.getDiscount,
        amount: formatPaymentDiscountAmount(calculation?.price ?? null, calculation?.discount_usd ?? null),
      },
      total: formatPaymentPrice(calculation?.final_price),
    }
  })

  const refInstallment = computed<DisciplineInstallmentSummary>(() => {
    const calculation = store.getSubscriptionCalculation

    return {
      maxPace: formatPaymentValue(calculation?.max_pace),
      lessonsPerPayment: formatPaymentValue(calculation?.payment_lessons),
      // What the first order really charges: a trial price with the discount on top, else the regular payment.
      firstPayment: formatPaymentPrice(calculation?.first_order_total),
      regularPayment: formatPaymentPrice(calculation?.one_payment),
      qty: formatPaymentValue(calculation?.payments_count),
      lessonsInPackage: formatPaymentValue(calculation?.lessons),
      totalPrice: formatPaymentPrice(calculation?.full_price),
      trialDiscount: calculation?.trial_discount
        ? {
          label: TRIAL_DISCOUNT_LABEL,
          percent: calculation.trial_discount,
          amount: formatPaymentDiscountAmount(calculation.trial_price, calculation.trial_discount_usd),
        }
        : null,
      discount: {
        label: DISCOUNT_LABEL,
        percent: store.getDiscount,
        amount: formatPaymentDiscountAmount(calculation?.full_price ?? null, calculation?.discount_usd ?? null),
      },
      total: formatPaymentPrice(calculation?.final_price),
    }
  })

  /** The one row of the price table, in the order of its columns. */
  const refCells = computed<string[]>(() => store.isOneTime
    ? [refOneTime.value.lessonsInPackage, refOneTime.value.totalPrice]
    : [
      refInstallment.value.maxPace,
      refInstallment.value.lessonsPerPayment,
      refInstallment.value.firstPayment,
      refInstallment.value.regularPayment,
      refInstallment.value.qty,
      refInstallment.value.lessonsInPackage,
      refInstallment.value.totalPrice,
    ])

  const generate = async (): Promise<void> => {
    const selectedId = refSelectedId.value
    if (!refCanGenerate.value || !selectedId) return

    const option = store.getPaymentOption
    const base: DisciplineOrderPayload = {
      products: [selectedId],
      discount: store.getDiscount,
      ...(store.getExpiresAt ? { expires_at: store.getExpiresAt } : {}),
    }
    const request = {
      disciplineId: route.params.id as string,
      onStart: () => {
        refIsGenerating.value = true
        store.setGenerating(true)
      },
      onFinish: () => {
        refIsGenerating.value = false
        store.setGenerating(false)
      },
    }

    const result = option === DisciplinePaymentOption.OneTime
      ? await useDisciplineOrderStoreApi({ ...request, payload: base })
      : await useDisciplineSubscriptionStoreApi({
        ...request,
        payload: { ...base, type: PAYMENT_SUBSCRIPTION_TYPE, auto_renew: store.isAutoRenew },
      })

    if (result) store.setLink({ option, orderId: result.order_id, link: result.link })
  }

  return {
    refIsOneTime,
    refIsCalculating,
    refIsGenerating,
    refCanGenerate,
    refOneTime,
    refInstallment,
    refCells,
    generate,
  }
}
